import { useCallback, useMemo, useState } from 'react';

import type { TranslatableLabel } from '#src/app/shared/hooks/i18n';

const RESET_LABEL_TIMER = 3000;

type ShareData = { clickedState: boolean; buttonLabel: TranslatableLabel };
type ShareActions = { afterShare: () => void };
type UseShareButtonStateResult = ModelHookResult<ShareData, ShareActions>;
type UseShareButtonStateArgs = {
	label: TranslatableLabel;
	successLabel?: TranslatableLabel;
};
/**
 * Resolves a share button's current label and clicked state, and exposes the `afterShare`
 * handler that temporarily swaps in `successLabel` before reverting to `label`.
 */
export function useShareButtonState({ label, successLabel }: UseShareButtonStateArgs): UseShareButtonStateResult {
	const [clickedState, setClickedState] = useState(false);

	const afterShare = useCallback(() => {
		if (successLabel) {
			setClickedState(true);
			// Timeout to reset the style
			window.setTimeout(() => {
				setClickedState(false);
			}, RESET_LABEL_TIMER);
		}
	}, [setClickedState, successLabel]);

	const buttonLabel = useMemo(
		() => (successLabel && clickedState ? successLabel : label),
		[clickedState, successLabel, label],
	);

	return {
		data: { clickedState, buttonLabel },
		actions: { afterShare },
	};
}
